import type { VisualState } from 'types'
import type { LivingFieldMode } from './living-field-engine'

export interface ResolveLivingFieldModeOptions {
  surface?: 'landing' | 'chat' | 'page'
  scrollProgress?: number
}

/**
 * Map Antigravity VisualState → living-field organism mode.
 * Priority: visual tools > voice out/in > research > ingest > think > make > idle.
 *
 * Chat: visitor talking is idle field (never hear-orb / speak-wave).
 * Assistant output may use restrained speak-wave. Thinking/search share search dust.
 * Vision immersive (see) stays off on chat.
 */
export function resolveLivingFieldMode(
  vs: VisualState,
  opts?: ResolveLivingFieldModeOptions
): LivingFieldMode {
  if (opts?.surface === 'landing') {
    return resolveLandingLivingFieldMode(opts.scrollProgress ?? 0, vs)
  }

  if (opts?.surface === 'chat') {
    return resolveChatLivingFieldMode(vs)
  }

  const tool = vs.toolCallData?.name?.toLowerCase() ?? ''
  const shape = vs.shape

  if (shape === 'error' || shape === 'shield') return 'error'
  if (vs.toolCallData?.state === 'error') return 'error'

  if (shape === 'screenshare' || tool.includes('screen') || tool.includes('screenshare')) {
    return 'screen'
  }

  if (shape === 'webcam' || tool.includes('webcam') || tool.includes('camera')) {
    return 'see'
  }

  if (vs.mode === 'speaking' || shape === 'wave' || shape === 'oscilloscope') {
    return 'speak'
  }

  if (vs.mode === 'listening') return 'hear'

  if (vs.researchActive) return 'search'

  if (shape === 'constellation' || shape === 'network' || shape === 'neural_network') {
    return 'search'
  }

  if (
    shape === 'scanner' ||
    tool.includes('file') ||
    tool.includes('upload') ||
    tool.includes('document')
  ) {
    return 'ingest'
  }

  if (shape === 'functionPulse' || tool.includes('booking') || tool.includes('calendar')) {
    return 'make'
  }

  if (vs.mode === 'thinking') return 'think'

  return 'idle'
}

function resolveChatLivingFieldMode(vs: VisualState): LivingFieldMode {
  const tool = vs.toolCallData?.name?.toLowerCase() ?? ''
  const shape = vs.shape

  if (shape === 'error' || shape === 'shield') return 'error'
  if (vs.toolCallData?.state === 'error') return 'error'

  if (shape === 'screenshare' || tool.includes('screenshare')) {
    return 'screen'
  }

  if (vs.researchActive) return 'search'

  if (
    shape === 'scanner' ||
    tool.includes('file') ||
    tool.includes('upload') ||
    tool.includes('document')
  ) {
    return 'ingest'
  }

  if (shape === 'functionPulse' || tool.includes('booking') || tool.includes('calendar')) {
    return 'make'
  }

  // Assistant output only — never visitor mic / listening.
  if (vs.mode === 'speaking') return 'speak'

  return 'idle'
}

/**
 * Landing narrative: hero opens on search grit, scroll reveals the rest.
 * Wave/speak is not the default marketing costume — only a soft hear late,
 * or real speak if a live voice session is somehow active.
 */
export function resolveLandingLivingFieldMode(scroll: number, vs: VisualState): LivingFieldMode {
  const shape = vs.shape
  const tool = vs.toolCallData?.name?.toLowerCase() ?? ''

  if (shape === 'error' || shape === 'shield') return 'error'
  if (vs.toolCallData?.state === 'error') return 'error'
  if (shape === 'screenshare' || tool.includes('screen')) return 'screen'
  if (shape === 'webcam' || tool.includes('webcam') || tool.includes('camera')) return 'see'
  if (shape === 'scanner' || tool.includes('file') || tool.includes('upload')) return 'ingest'

  // Live voice on landing is rare; allow it, but never treat idle `wave` as speak.
  if (vs.mode === 'speaking' && vs.isActive) return 'speak'
  if (vs.mode === 'listening' && vs.isActive) return 'hear'

  // Section / hover cues (from LandingPage intersection + cards)
  if (shape === 'constellation' || shape === 'network' || shape === 'neural_network')
    return 'search'
  if (shape === 'brain') return 'think'
  if (shape === 'grid' || shape === 'dna') return 'see'
  if (shape === 'hourglass' || shape === 'functionPulse') return 'make'

  const s = Math.max(0, Math.min(1, scroll))
  if (s < 0.18) return 'search'
  if (s < 0.36) return 'think'
  if (s < 0.52) return 'see'
  if (s < 0.68) return 'ingest'
  if (s < 0.84) return 'hear'
  return 'idle'
}

export function resolveLivingFieldCount(
  surface: 'landing' | 'chat' | 'page',
  handheld: boolean,
  reducedMotion: boolean
): number {
  if (reducedMotion) return handheld ? 220 : 360
  if (surface === 'chat') return handheld ? 360 : 760
  if (surface === 'landing') return handheld ? 520 : 1100
  return handheld ? 560 : 1100
}
